import { useCallback, useMemo } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Alert,
  Box,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  useGetV2AdminOrganizationsByOrganizationIdPermissionsQuery,
  usePatchV2AdminOrganizationsByOrganizationIdPermissionsMutation,
} from '../store/hooks';

interface OrganizationRolePermissionsSectionProps {
  organizationId: string;
  /**
   * 'edit' (default): show only configurable rows as switches — the editor.
   * 'read': show the full permission catalog as check / dash icons — the reference.
   */
  mode?: 'edit' | 'read';
}

interface PermissionRoleGrant {
  role: string;
  label: string;
  enabled: boolean;
  configurable?: boolean;
}

interface PermissionMatrixItem {
  permission: string;
  label: string;
  description?: string | null;
  configurable: boolean;
  roles?: PermissionRoleGrant[];
}

function buildPermissionsPayload(
  permissions: PermissionMatrixItem[],
  permissionId: string,
  roleId: string,
  enabled: boolean,
) {
  const payload: Record<string, Record<string, boolean>> = {};

  // Only configurable cells may appear in the PATCH — the backend rejects
  // overrides for restricted role cells (e.g. view_all_notes for supervisor).
  for (const permission of permissions) {
    payload[permission.permission] = {};
    for (const roleGrant of permission.roles ?? []) {
      if (roleGrant.configurable) {
        payload[permission.permission][roleGrant.role] = roleGrant.enabled;
      }
    }
  }

  payload[permissionId] = {
    ...(payload[permissionId] ?? {}),
    [roleId]: enabled,
  };

  return payload;
}

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = (error as { data?: { message?: unknown } }).data;
    if (typeof data?.message === 'string') {
      return data.message;
    }
  }

  return 'Failed to update role permissions';
}

export function OrganizationRolePermissionsSection({
  organizationId,
  mode = 'edit',
}: OrganizationRolePermissionsSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError, refetch } =
    useGetV2AdminOrganizationsByOrganizationIdPermissionsQuery(
      { organizationId },
      { skip: !organizationId },
    );
  const [updatePermissions, { isLoading: isUpdating }] =
    usePatchV2AdminOrganizationsByOrganizationIdPermissionsMutation();

  const permissions = useMemo(
    () => (data?.permissions ?? []) as PermissionMatrixItem[],
    [data?.permissions],
  );

  const configurablePermissions = useMemo(
    () => permissions.filter(permission => permission.configurable),
    [permissions],
  );
  // Editor mode shows only configurable rows; reference mode shows the full
  // catalog. Column headers come from the rendered subset.
  const visiblePermissions = mode === 'edit' ? configurablePermissions : permissions;
  const headerRoles = visiblePermissions[0]?.roles ?? [];

  const handleToggle = useCallback(
    async (permissionId: string, roleId: string, enabled: boolean) => {
      const nextPermissions = buildPermissionsPayload(
        configurablePermissions,
        permissionId,
        roleId,
        enabled,
      );

      try {
        await updatePermissions({
          organizationId,
          adminUpdatePermissionsMatrixRequest: {
            permissions: nextPermissions,
          },
        }).unwrap();
        enqueueSnackbar('Role permissions updated', { variant: 'success' });
        refetch();
      } catch (error: unknown) {
        enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      }
    },
    [configurablePermissions, enqueueSnackbar, organizationId, refetch, updatePermissions],
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <TuneIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
          Role Permissions
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {mode === 'edit'
          ? 'Configure organization-specific role access.'
          : 'What each role can do in this organization.'}
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      ) : (
        <>
          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Role permissions could not be loaded.
            </Alert>
          )}

          {visiblePermissions.length === 0 ? (
            <Alert severity="info">
              {mode === 'edit'
                ? 'No configurable role permissions are available.'
                : 'No role permissions are available.'}
            </Alert>
          ) : (
            <TableContainer
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflowX: 'auto',
              }}
            >
              <Table size="small" sx={{ minWidth: 720, tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '26%' }} />
                  {headerRoles.map(role => (
                    <col key={role.role} style={{ width: `${74 / (headerRoles.length || 1)}%` }} />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Permission</TableCell>
                    {headerRoles.map(role => (
                      <TableCell key={role.role} align="center" sx={{ fontWeight: 600 }}>
                        {role.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiblePermissions.map(permission => (
                    <TableRow key={permission.permission}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {permission.label}
                        </Typography>
                        {permission.description && (
                          <Typography variant="caption" color="text.secondary">
                            {permission.description}
                          </Typography>
                        )}
                      </TableCell>
                      {(permission.roles ?? []).map(role => (
                        <TableCell
                          key={role.role}
                          align="center"
                          aria-label={
                            mode === 'edit' && role.configurable
                              ? undefined
                              : `${permission.label} for ${role.label}: ${role.enabled ? 'granted' : 'not granted'}`
                          }
                        >
                          {mode === 'edit' && role.configurable ? (
                            <Switch
                              checked={role.enabled}
                              disabled={isUpdating}
                              onChange={event =>
                                handleToggle(permission.permission, role.role, event.target.checked)
                              }
                              slotProps={{
                                input: {
                                  'aria-label': `Toggle ${permission.label} for ${role.label}`,
                                },
                              }}
                            />
                          ) : role.enabled ? (
                            <CheckIcon color="success" fontSize="small" />
                          ) : (
                            <RemoveIcon color="disabled" fontSize="small" />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {isUpdating && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Updating...
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
